import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { AlertCircle, Mail, Clock, CheckCircle2, XCircle } from 'lucide-react';

/** Owner + Design Ops surface showing every outbound email the system tried
 *  (or would have tried) to send. When the Google Workspace service account
 *  isn't wired up yet, everything sits here with status='queued_no_provider'
 *  so nothing is silently lost. */
export default function OutboxPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, queued_no_provider: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/outbox').then(r => {
      if (!alive) return;
      setRows(r.data.rows || []);
      setTotals({ total: r.data.total || 0, queued_no_provider: r.data.queued_no_provider || 0 });
    }).catch(e => toast.error(parseErr(e))).finally(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  if (user && !['owner', 'operations'].includes(user.role)) return <Navigate to="/" replace />;

  return (
    <AppShell breadcrumbs={[{ label: 'Outbox' }]}>
      <div className="p-6 max-w-[1120px] mx-auto fade-up" data-testid="outbox-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-heading text-[26px] font-semibold leading-tight">Email outbox</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              Every notification and approval decision the app tries to send.
              When Gmail isn't wired yet, rows sit here safely for reference.
            </p>
          </div>
        </div>

        {totals.queued_no_provider > 0 && (
          <div className="mb-4 rounded-md p-3 flex items-start gap-2 border"
               style={{ borderColor: 'hsl(var(--warning)/.35)', background: 'hsl(var(--warning)/.08)' }}
               data-testid="outbox-provider-banner">
            <AlertCircle size={14} className="mt-0.5 text-[hsl(var(--warning))]" />
            <div className="text-[12.5px]">
              <strong>Gmail sender not configured.</strong> {totals.queued_no_provider} message
              {totals.queued_no_provider === 1 ? '' : 's'} queued for delivery.
              Ask your Workspace admin to set <code className="text-[11.5px]">GOOGLE_SERVICE_ACCOUNT_JSON</code> and
              <code className="text-[11.5px]"> GMAIL_SENDER_EMAIL</code> on the backend — messages will start sending automatically.
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[13px] text-[hsl(var(--foreground-muted))] py-10 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border p-10 text-center" style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="outbox-empty">
            <Mail className="mx-auto mb-2 text-[hsl(var(--foreground-muted))]" size={22} />
            <div className="text-[13px] font-semibold">No emails sent yet.</div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">
              Approval decisions and system notifications will appear here.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="outbox-list">
            {rows.map(r => <OutboxRow key={r.id} row={r} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function OutboxRow({ row }) {
  const statusBadge = (
    row.status === 'sent' ? { icon: CheckCircle2, cls: 'bg-[hsl(var(--success)/.12)] text-[hsl(var(--success))]', label: 'Sent' } :
    row.status === 'error' ? { icon: XCircle, cls: 'bg-[hsl(var(--destructive)/.12)] text-[hsl(var(--destructive))]', label: 'Failed' } :
    { icon: Clock, cls: 'bg-[hsl(var(--warning)/.12)] text-[hsl(var(--warning))]', label: 'Queued' }
  );
  const StatusIcon = statusBadge.icon;
  return (
    <div className="rounded-lg border px-4 py-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`outbox-row-${row.id}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded ${statusBadge.cls}`}>
          <StatusIcon size={10} />{statusBadge.label}
        </span>
        <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{row.kind}</span>
        <span className="ml-auto text-[11px] text-[hsl(var(--foreground-subtle))]">{row.created_at?.slice(0, 16).replace('T', ' · ')}</span>
      </div>
      <div className="text-[13px] font-medium mt-1 truncate">{row.subject}</div>
      <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5 truncate">
        to {row.to.join(', ')}{row.provider_message_id ? ` · ${row.provider_message_id}` : ''}
      </div>
      {row.error && (
        <div className="text-[11px] text-[hsl(var(--destructive))] mt-1">{row.error}</div>
      )}
    </div>
  );
}
